import React, { useState } from 'react'
import { ModalBase } from '@/components/modals/ModalBase'
import { transferService } from '@/services/transferService'

interface Props {
  isOpen: boolean
  onClose: () => void
  enrollmentId: string
  onSuccess?: () => void
}

/**
 * TransferTicketModal — M5 one-shot ticket transfer UI.
 * Caller is responsible for only mounting this when eligible
 * (transfer_count = 0, batch not started). The backend re-validates too.
 */
export const TransferTicketModal: React.FC<Props> = ({ isOpen, onClose, enrollmentId, onSuccess }) => {
  const [targetEmail, setTargetEmail] = useState('')
  const [targetName, setTargetName] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    setError(null)
    setSuccess(null)
    if (!targetEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(targetEmail)) {
      setError('Email tujuan tidak valid')
      return
    }
    setSubmitting(true)
    try {
      await transferService.transfer(enrollmentId, {
        targetEmail: targetEmail.trim(),
        targetName: targetName.trim() || undefined,
        reason: reason.trim() || undefined,
      })
      setSuccess('Tiket ditransfer. Email undangan telah dikirim.')
      onSuccess?.()
    } catch (e) {
      setError((e as Error).message || 'Gagal transfer tiket')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalBase isOpen={isOpen} onClose={onClose}>
      <h2 className="text-xl font-semibold mb-2">Transfer Tiket</h2>
      <p className="text-sm text-gray-500 mb-4">
        Pindahkan tiket ke email lain. Hanya bisa dilakukan satu kali dan sebelum event dimulai.
      </p>

      <div className="space-y-3">
        <label className="block">
          <span className="text-sm font-medium">Email tujuan *</span>
          <input
            type="email"
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            className="w-full mt-1 border rounded px-3 py-2"
            data-testid="transfer-target-email"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Nama tujuan (opsional)</span>
          <input
            type="text"
            value={targetName}
            onChange={(e) => setTargetName(e.target.value)}
            className="w-full mt-1 border rounded px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Alasan (opsional)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full mt-1 border rounded px-3 py-2"
          />
        </label>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-600 mt-3">
          {error}
        </p>
      )}
      {success && (
        <p role="status" className="text-sm text-green-600 mt-3">
          {success}
        </p>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
          Tutup
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="px-4 py-2 bg-primary text-white rounded disabled:opacity-50"
          data-testid="transfer-submit"
        >
          {submitting ? 'Memproses...' : 'Transfer'}
        </button>
      </div>
    </ModalBase>
  )
}

export default TransferTicketModal
